import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // status: "loading" | "authenticated" | "anonymous"
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);

  // Hydrate from a stored token on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setStatus("anonymous");
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        // /me refreshes the token, update local storage
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setUser(data.user);
        setTenant(data.tenant);
        setStatus("authenticated");
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setTenant(null);
        setStatus("anonymous");
      });
  }, []);

  const persistSession = useCallback((authResponse) => {
    localStorage.setItem(TOKEN_KEY, authResponse.access_token);
    setUser(authResponse.user);
    setTenant(authResponse.tenant);
    setStatus("authenticated");
  }, []);

  const signIn = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      persistSession(data);
      return data;
    },
    [persistSession]
  );

  const signUp = useCallback(
    async ({ email, password, name, tenant_name }) => {
      const { data } = await api.post("/auth/signup", {
        email,
        password,
        name,
        tenant_name,
      });
      persistSession(data);
      return data;
    },
    [persistSession]
  );

  const acceptInvite = useCallback(
    async (token, { name, password }) => {
      const { data } = await api.post(`/invites/${token}/accept`, {
        name,
        password,
      });
      persistSession(data);
      return data;
    },
    [persistSession]
  );

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setTenant(null);
    setStatus("anonymous");
  }, []);

  const refreshTenant = useCallback(async () => {
    const { data } = await api.get("/tenants/me");
    setTenant(data);
    return data;
  }, []);

  const refreshMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    setTenant(data.tenant);
    return data;
  }, []);

  const value = useMemo(
    () => ({
      status,
      user,
      tenant,
      signIn,
      signUp,
      acceptInvite,
      signOut,
      refreshTenant,
      refreshMe,
      setTenant,
    }),
    [status, user, tenant, signIn, signUp, acceptInvite, signOut, refreshTenant, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
